// Product Control Plane — one product: published state, what the tier is
// actually serving, draft state, revision history, and rollback.
//
// PUBLISHED and DRAFT are the two facts an operator must never mistake for
// each other, so they are two bands of equal weight at the top of the page
// rather than a badge somewhere. Everything below them is history.
//
// SERVING is a third fact and belongs to neither. Published and draft are both
// records of intent held by this service; serving is what a device gets when
// it asks. This page used to print the published revision under the heading
// "what devices render now", which asserted the third fact from the first.
// The two are now separate cards and a status word names the gap.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  bootstrap,
  bootstrapPreview,
  createProposal,
  getProduct,
  getRevision,
  listRevisions,
  rollback,
  type BootstrapPreviewResponse,
  type ProductDetailResponse,
  ownsPublishableDocument,
  type RevisionSummary,
  type RevisionsResponse,
} from "../controlPlane/api";
import { type ControlPlaneDocument, type Lane } from "../controlPlane/contract";
import { diffDocuments, renderValue, rowsOf, rowVisibility, summarize } from "../controlPlane/diff";
import { classifyControlPlaneError, failureLine } from "../controlPlane/errors";
import { GovernedAction } from "../controlPlane/GovernedAction";
import { observeServedRevision, type Observation } from "../controlPlane/observed";
import { syncLabel, syncState } from "../controlPlane/sync";
import { laneHost, laneLabel, useControlPlaneTheme } from "../controlPlane/theme";
import { ActionCard, ActionRow, WorkflowStepper } from "../ui/Workflow";
import { IconEdit, IconHistory, IconPublish, IconRollback } from "../ui/icons";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_LABEL,
  GAOSAFE_APPROVAL_REASON,
  useControlPlaneWritesBlocked,
} from "../authority";
import {
  Btn,
  Card,
  CpHeader,
  Empty,
  ErrorNote,
  Hash,
  Kv,
  KvList,
  LaneChip,
  StatusChip,
} from "../controlPlane/ui";
import { tdStyle, thStyle } from "../controlPlane/styles";

/** Page-level read failures. A refused governed WRITE is classified inside
 *  `GovernedAction`, which can say which of the eleven refusals it was. */
function describeError(e: unknown): string {
  return failureLine(classifyControlPlaneError(e));
}

export function ControlPlaneProduct() {
  const { productId = "" } = useParams();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [detail, setDetail] = useState<ProductDetailResponse | null>(null);
  const [revisions, setRevisions] = useState<RevisionsResponse | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  // Rollback needs the TARGET document before it can preview anything, so the
  // dialog is opened only once that fetch resolves. A rollback confirmation
  // that cannot say what will change is not a confirmation.
  const [rollbackTarget, setRollbackTarget] = useState<{
    revision: RevisionSummary;
    document: ControlPlaneDocument;
  } | null>(null);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  /** What the SERVER says a bootstrap would publish, and the digest its
   *  approval will commit to. `null` until asked; a failure is a state to
   *  show, because an unreviewed genesis is a reason to stop. */
  const [genesis, setGenesis] = useState<BootstrapPreviewResponse | null>(null);
  const [genesisError, setGenesisError] = useState<string | null>(null);

  // What the tier actually serves. `null` means "not asked yet", which is a
  // different state from "asked and could not read" and must not collapse
  // into it — one is a page that has not finished loading, the other is a
  // finding.
  const [observed, setObserved] = useState<Observation | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, r] = await Promise.all([
        getProduct(productId),
        listRevisions(productId).catch(() => null),
      ]);
      setDetail(d);
      setRevisions(r);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Observed separately from `load`, and never awaited by it: the operator
  // surface must render whether or not the serving tier answers, and a failed
  // observation is a result to display rather than an error that blanks the
  // page.
  const observe = useCallback(async () => {
    setObserved(null);
    setObserved(await observeServedRevision(productId));
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void observe();
  }, [observe]);

  const lane: Lane | null = detail?.lane ?? null;
  const published = detail?.published ?? null;

  // Compared against the POINTER, not against `published.revisionId`. They are
  // normally the same value, but the pointer is the thing the tier is supposed
  // to be following, and after a rollback it is the pointer that moved.
  const sync = syncState(detail?.current?.revisionId ?? null, observed);

  const visibleCount = useMemo(() => {
    if (!published) return null;
    const items = rowsOf(published.document);
    // Probe with a very high version so the count reports lane visibility
    // rather than "hidden from a client that declared nothing", which is true
    // of every version-gated row and tells an operator nothing.
    if (lane === null) return { visible: null, total: items.length };
    const visible = items.filter(
      (i) => rowVisibility(i, lane, "999999.0.0").visible,
    ).length;
    return { visible, total: items.length };
  }, [published, lane]);

  const rollbackDiff = useMemo(() => {
    if (!rollbackTarget || !published) return null;
    return diffDocuments(
      published.document,
      rollbackTarget.document,
      lane === null ? undefined : [lane],
    );
  }, [rollbackTarget, published, lane]);

  /** Every field the pointer move would change, flattened for a compact list.
   *  Built from the SAME `DocumentDiff` the summary counts come from, so the
   *  list and the counts can never disagree. */
  const rollbackChanges = useMemo(() => {
    if (!rollbackDiff) return [];
    const out: Array<{ key: string; itemId: string; detail: string }> = [];
    for (const a of rollbackDiff.added) {
      out.push({ key: `a-${a.item.id}`, itemId: a.item.id, detail: `returns — ${a.item.title}` });
    }
    for (const r of rollbackDiff.removed) {
      out.push({ key: `r-${r.item.id}`, itemId: r.item.id, detail: "is removed from the document" });
    }
    for (const m of rollbackDiff.modified) {
      out.push({
        key: `m-${m.after.id}`,
        itemId: m.after.id,
        detail: m.changes
          .map((c) => `${c.field}: ${renderValue(c.before)} \u2192 ${renderValue(c.after)}`)
          .join(", "),
      });
    }
    for (const o of rollbackDiff.reordered) {
      out.push({
        key: `o-${o.item.id}`,
        itemId: o.item.id,
        detail: `position ${o.fromPosition + 1} \u2192 ${o.toPosition + 1}`,
      });
    }
    return out;
  }, [rollbackDiff]);

  /** How many publishes separate the live revision from the rollback target.
   *  A rollback of one is routine; a rollback of five walks past four
   *  revisions someone deliberately published, and an operator is entitled to
   *  know which of those they are doing before they ask for an approval. */
  const rollbackDistance = useMemo(() => {
    if (!rollbackTarget || !revisions) return null;
    const currentSeq = revisions.revisions.find((r) => r.isCurrent)?.revisionSeq;
    if (currentSeq === undefined) return null;
    return currentSeq - rollbackTarget.revision.revisionSeq;
  }, [rollbackTarget, revisions]);

  async function openRollback(revision: RevisionSummary) {
    try {
      const rev = await getRevision(productId, revision.revisionId);
      setRollbackTarget({ revision, document: rev.document });
    } catch (e) {
      setError(describeError(e));
    }
  }

  const loadGenesis = useCallback(async () => {
    setGenesisError(null);
    setGenesis(null);
    try {
      setGenesis(await bootstrapPreview(productId));
    } catch (e) {
      setGenesisError(describeError(e));
    }
  }, [productId]);

  const hasDraftChanges = detail?.draft ? !detail.draft.matchesPublished : false;
  const writesBlocked = useControlPlaneWritesBlocked();

  // The console offers the governed-write affordances (bootstrap, edit,
  // publish, rollback) only for an entry the genesis catalog carries. That
  // verdict is the SERVER's — `product.catalog`, the same one the registry and
  // the genesis derivation act on — read here, never recomputed: a capability,
  // an infrastructure surface, a future/retired entry, and an entry with no
  // URL are all reported ineligible, and the control plane stores no served
  // document for any of them. `product.catalog.reason` is the sentence to
  // show. Reading the verdict (not re-deriving it from kind/status) is what
  // keeps this console from being a second implementation of the rule.
  // …and a product that declares its OWN document type (gao-local-ai) owns one
  // although it is not an Explorer entry: the server's `product.document`
  // verdict answers that separately (`ownsPublishableDocument`).
  const writableProduct = detail !== null && ownsPublishableDocument(detail.product);
  const notAProductReason =
    detail !== null && !writableProduct && detail.product.catalog?.eligible === false
      ? detail.product.catalog.reason
      : null;

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <CpHeader
        breadcrumb={<Link to="/control-plane">Control Plane</Link>}
        title={productId}
        subtitle={detail?.product.displayName}
        actions={
          <>
            <LaneChip lane={lane} host={laneHost(lane)} />
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
            <button type="button" onClick={() => void load()} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </>
        }
      />

      {error !== null && <ErrorNote>{error}</ErrorNote>}
      {notice !== null && (
        <Card accent="ok">
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>{notice}</div>
            <Btn onClick={() => setNotice(null)}>Dismiss</Btn>
          </div>
        </Card>
      )}

      {/* ── Actions first ──────────────────────────────────────────────
          The four verbs an operator opens this page to perform. They were
          previously three small links buried inside the Draft card and one
          button per row at the bottom of a revision table, so "what can I do
          here" was answered by scanning the whole page.

          Every card maps to a real endpoint (or to the route that owns it):
          EDIT CONTENT → the draft editor, PUBLISH → the review screen which
          holds the diff, the validation report and the typed confirmation,
          ROLLBACK → the revision history where a target must be chosen, and
          VIEW HISTORY → the audit trail. PUBLISH and ROLLBACK deliberately
          NAVIGATE rather than act: neither is safe as a one-click card,
          because publish needs an expectedBaseRevisionId and a diff the
          operator has actually seen, and rollback has no meaning without a
          chosen revision. */}
      {writesBlocked && <AuthorityNotice scope="Control Plane writes are blocked." />}

      {/* A registry entry that is not a current standalone product carries no
          publishable document. Rather than show governed-write affordances that
          would mint a revision no device is served, the page says why — in the
          server's words — and offers only the read trail. */}
      {detail && !writableProduct && (
        <Card accent="warn">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontWeight: 640 }}>
              This entry has no publishable document
            </div>
            <p style={{ margin: 0, color: "var(--fg-muted)" }}>
              {notAProductReason ??
                `${detail.product.displayName} is not a current standalone product.`}{" "}
              Bootstrap, edit, publish and rollback apply to a product the
              genesis catalog carries; this entry is recorded in the registry
              but the control plane stores no document for it, and the consumer
              route does not serve one.
            </p>
            <div>
              <Link to={`/control-plane/${productId}/audit`}>View history</Link>
            </div>
          </div>
        </Card>
      )}

      {detail && writableProduct && (
        <>
          <ActionRow>
            <ActionCard
              label="Edit content"
              hint={
                detail.draft
                  ? hasDraftChanges
                    ? "Draft has pending changes"
                    : "Draft is in step with live"
                  : "Start a draft from the live revision"
              }
              icon={<IconEdit size={18} />}
              to={writesBlocked ? undefined : `/control-plane/${productId}/edit`}
              disabled={writesBlocked}
              disabledReason={GAOSAFE_APPROVAL_LABEL}
            />
            {/* NOT gated on the freeze. The destination is the REVIEW
                screen — the diff, the validation report, the risk class, the
                approval requirement and the proposal cycle — and every one of
                those is a read. The rule this keeps is the one that mattered:
                a screen must not say "you cannot start this" and "click here
                to start it" at once. It does not say the first, because the
                destination is not the act. Publishing itself stays refused,
                on the screen that owns it, in the words the server uses. */}
            <ActionCard
              label="Publish"
              tone={hasDraftChanges ? "accent" : "neutral"}
              icon={<IconPublish size={18} />}
              to={`/control-plane/${productId}/review`}
              hint={
                writesBlocked
                  ? "Review the diff and the approval requirement"
                  : hasDraftChanges
                    ? "Review the diff, then publish"
                    : "Review changes"
              }
              disabled={!hasDraftChanges}
              disabledReason="No unpublished changes to publish"
            />
            <ActionCard
              label="Roll back"
              tone="danger"
              icon={<IconRollback size={18} />}
              onClick={() => {
                historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              hint={
                writesBlocked
                  ? "Review a restore target and its approval"
                  : "Choose a revision to restore"
              }
              disabled={(revisions?.revisions.length ?? 0) < 2}
              disabledReason="Needs a second revision to roll back to"
            />
            <ActionCard
              label="View history"
              icon={<IconHistory size={18} />}
              to={`/control-plane/${productId}/audit`}
              hint="Publication and rollback trail"
            />
          </ActionRow>

          {/* The stepper narrates where the draft stands, which stays useful
              while writes are frozen. What it must NOT do is hand the operator
              a link into the publish workflow that the action cards directly
              above have just refused — one screen saying both "you cannot start
              this" and "click here to start it". Under the freeze every step
              keeps its state and loses its link. */}
          <WorkflowStepper
            label="Control Plane publish workflow"
            steps={[
              {
                key: "draft",
                label: "Draft",
                to: writesBlocked ? undefined : `/control-plane/${productId}/edit`,
                state: hasDraftChanges ? "done" : "available",
                hint: hasDraftChanges ? "Changes pending" : "No changes",
              },
              {
                key: "validate",
                label: "Validate",
                to:
                  hasDraftChanges && !writesBlocked
                    ? `/control-plane/${productId}/review`
                    : undefined,
                // Never "done": `validateDraft` result is not persisted
                // anywhere, so this page cannot know whether it ever ran.
                state: hasDraftChanges ? "available" : "blocked",
                hint: hasDraftChanges ? "Runs on review" : undefined,
              },
              {
                key: "review",
                label: "Review",
                // Reachable under the freeze: reviewing is what a frozen
                // console is FOR. Publishing is not, and is refused there.
                to: hasDraftChanges ? `/control-plane/${productId}/review` : undefined,
                state: hasDraftChanges ? "current" : "blocked",
                hint: hasDraftChanges ? "Diff against live" : undefined,
              },
              {
                key: "publish",
                label: "Publish",
                to:
                  hasDraftChanges && !writesBlocked
                    ? `/control-plane/${productId}/review`
                    : undefined,
                state: hasDraftChanges ? "available" : published !== null ? "done" : "blocked",
                hint: writesBlocked
                  ? GAOSAFE_APPROVAL_LABEL
                  : published !== null && !hasDraftChanges
                    ? `Live: ${published.revisionId}`
                    : undefined,
              },
            ]}
          />
        </>
      )}

      {detail && writableProduct && published === null && detail.current === null && (
        <Card accent="warn" title="No revision has ever been published">
          <p style={{ margin: "0 0 10px" }}>
            Until a first revision exists, <code>/v2/explorer/sites</code> is
            served from the legacy <code>explorer_sites</code> table, so no
            installed app is affected. Bootstrapping reads the Product Registry
            exactly as it stands, carries every current standalone product,
            hashes the real bytes, and publishes them as{" "}
            <code>rev_000001</code>. Every registry entry it does not carry is
            reported with the reason, never dropped silently.
          </p>
          <Btn
            tone="primary"
            onClick={() => {
              setBootstrapOpen(true);
              void loadGenesis();
            }}
            // Opens the REVIEW, which is a read. The act below it is gated.
            disabled={bootstrapOpen}
          >
            Review the genesis bootstrap…
          </Btn>
        </Card>
      )}

      {detail?.publishedError && published === null && detail.current !== null && (
        <ErrorNote>
          The live revision could not be read: <code>{detail.publishedError}</code>.
          Consumers are being served a fail-closed 503 rather than a document
          this service cannot vouch for.
        </ErrorNote>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        <Card accent="ok" title="Published — the revision this tier should serve">
          {published === null ? (
            <Empty>Nothing published.</Empty>
          ) : (
            <>
              <div style={{ fontSize: 22, margin: "0 0 10px" }}>
                {published.revisionId}
              </div>
              <KvList>
                <Kv k="published">{published.publishedAt}</Kv>
                <Kv k="by">
                  {published.publishedBy}
                  {published.publishedByLabel && (
                    <span style={{ color: "var(--fg-dim)" }}>
                      {" "}
                      {published.publishedByLabel}
                    </span>
                  )}
                </Kv>
                <Kv k="hash">
                  <Hash value={published.documentHash} />
                </Kv>
                <Kv k="pointer">
                  moved {detail?.current?.pointerSeq ?? 1}×
                </Kv>
                {visibleCount && (
                  <Kv k="rows">
                    {visibleCount.visible === null
                      ? `${visibleCount.total} — lane unknown, so the visible count is unknown`
                      : `${visibleCount.visible} of ${visibleCount.total} pass the lane filter`}
                  </Kv>
                )}
              </KvList>
            </>
          )}
        </Card>

        <Card
          accent={
            sync.tone === "ok" ? "ok" : sync.tone === "danger" ? "danger" : "warn"
          }
          title="Serving — what this tier returns now"
        >
          <div style={{ margin: "0 0 10px" }}>
            <StatusChip tone={sync.tone}>{syncLabel(sync.status)}</StatusChip>
          </div>
          <p style={{ margin: "0 0 10px", color: "var(--fg-dim)" }}>{sync.detail}</p>
          <KvList>
            <Kv k="observed">
              {observed === null
                ? "asking…"
                : observed.kind === "serving"
                  ? observed.revision
                  : observed.kind === "not_serving"
                    ? "no document"
                    : "could not read"}
            </Kv>
            {observed?.kind === "serving" && (
              <Kv k="published">{observed.publishedAt}</Kv>
            )}
            <Kv k="source">
              unauthenticated <code>GET /v2/control-plane/products/{productId}</code>
            </Kv>
          </KvList>
          <p style={{ margin: "10px 0 0", fontSize: 11, color: "var(--fg-dim)" }}>
            This is the tier this console is pointed at, read the way a device
            reads it. It is not a report from any installed app — no Gao client
            reports the revision it loaded, so what a device holds is still
            unobserved. A cache honouring the route&rsquo;s five-minute freshness
            window can return a document that old.
          </p>
          <div style={{ marginTop: 10 }}>
            <Btn onClick={() => void observe()} disabled={observed === null}>
              Re-check
            </Btn>
          </div>
        </Card>

        <Card
          accent={hasDraftChanges ? "warn" : undefined}
          title={
            hasDraftChanges
              ? "Draft — not visible to anyone"
              : "Draft — no unpublished changes"
          }
        >
          {detail?.draft ? (
            <>
              <div
                style={{
                  fontSize: 22,
                  margin: "0 0 10px",
                  color: hasDraftChanges ? "var(--warn)" : "var(--fg-dim)",
                }}
              >
                {hasDraftChanges ? "Changes pending" : "In step with live"}
              </div>
              <KvList>
                <Kv k="edited">{detail.draft.updatedAt}</Kv>
                <Kv k="by">{detail.draft.updatedBy}</Kv>
                <Kv k="based on">{detail.draft.baseRevisionId ?? "—"}</Kv>
                <Kv k="hash">
                  <Hash value={detail.draft.documentHash} />
                </Kv>
              </KvList>
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                {writesBlocked ? (
                  <Btn disabled title={GAOSAFE_APPROVAL_REASON}>
                    Edit rows
                  </Btn>
                ) : (
                  <Link to={`/control-plane/${productId}/edit`}>
                    <Btn>Edit rows</Btn>
                  </Link>
                )}
                {writesBlocked ? (
                  <Btn disabled title={GAOSAFE_APPROVAL_REASON}>
                    Review &amp; publish…
                  </Btn>
                ) : (
                  <Link to={`/control-plane/${productId}/review`}>
                    <Btn tone={hasDraftChanges ? "primary" : "default"}>
                      Review &amp; publish…
                    </Btn>
                  </Link>
                )}
                <Link to={`/control-plane/${productId}/audit`}>
                  <Btn>Audit</Btn>
                </Link>
              </div>
            </>
          ) : (
            <Empty>No draft.</Empty>
          )}
        </Card>
      </div>

      {/* The Roll back action card scrolls here rather than acting: a
          rollback has no meaning without a chosen target revision, so the
          card's job is to put the operator in front of the list that has
          them. The per-row "Roll back to…" buttons still own the action. */}
      <div ref={historyRef} />
      <Card
        title="Revision history"
        note="immutable — rollback moves the live pointer, nothing is rewritten"
        style={{ padding: 0 }}
      >
        {!revisions || revisions.revisions.length === 0 ? (
          <Empty>No revisions.</Empty>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>State</th>
                  <th style={thStyle}>Revision</th>
                  <th style={thStyle}>Seq</th>
                  <th style={thStyle}>Published</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>Hash</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {revisions.revisions.map((r) => (
                  <tr key={r.revisionId}>
                    <td style={tdStyle}>
                      {r.isCurrent ? (
                        <StatusChip tone="ok">LIVE</StatusChip>
                      ) : (
                        <span style={{ color: "var(--fg-dim)" }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>{r.revisionId}</td>
                    <td style={{ ...tdStyle, color: "var(--fg-dim)" }}>
                      {r.revisionSeq}
                    </td>
                    <td style={{ ...tdStyle, color: "var(--fg-dim)" }}>
                      {r.publishedAt}
                    </td>
                    <td style={{ ...tdStyle, color: "var(--fg-dim)" }}>
                      {r.publishedBy}
                    </td>
                    <td style={tdStyle}>
                      <Hash value={r.documentHash} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {r.isCurrent ? null : (
                        <Btn
                          onClick={() => void openRollback(r)}
                          // NOT gated on `writesBlocked`. This selects a
                          // rollback target and loads its document; it is a
                          // read. Gating it meant a frozen console could not
                          // even show an operator what a rollback WOULD do,
                          // which is the preparation the freeze exists to
                          // allow. The act itself is gated below, where it is.
                          disabled={rollbackTarget?.revision.revisionId === r.revisionId}
                        >
                          Review rollback…
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── §18 ROLLBACK ────────────────────────────────────────────────
          A rollback moves the live pointer to an EXISTING immutable revision.
          It is never an edit backwards: no revision is rewritten, the revision
          being left stays in the history as having been live, and the next
          publish takes the next number rather than reusing one.

          The review is rendered as soon as a target is chosen — under the
          freeze too — because choosing a target is a read and preparing is
          the whole point of a frozen console. */}
      {rollbackTarget !== null && (
        <GovernedAction
          act={{
            operation: "rollback",
            productId,
            lane,
            // The proposal commits to the revision the pointer will name, so
            // an approval to roll back to one revision cannot be spent
            // rolling back to another.
            target: rollbackTarget.revision.revisionId,
          }}
          title={`Roll back to ${rollbackTarget.revision.revisionId}`}
          tone={lane === "production" ? "danger" : "warning"}
          executeLabel={`Roll back to ${rollbackTarget.revision.revisionId}`}
          blockedReason={
            published === null
              ? detail?.current !== null && detail?.publishedError
                ? `The live revision cannot be read (${detail.publishedError}), so there is nothing to show a rollback against.`
                : "Nothing is published, so there is nothing to roll back from."
              : null
          }
          consequence={
            <div>
              This moves the live pointer only. <b>No revision is deleted or
              rewritten</b>, and the revision being rolled away from stays in
              the history as having been live. A publish after this creates the
              next number — it never reuses one.
            </div>
          }
          rows={[
            { key: "action", value: "rollback" },
            { key: "product", value: productId },
            { key: "environment", value: laneLabel(lane) },
            {
              key: "current revision",
              value: published?.revisionId ?? detail?.current?.revisionId ?? "—",
            },
            {
              key: "target revision",
              value: (
                <>
                  <b>{rollbackTarget.revision.revisionId}</b>{" "}
                  <span style={{ color: "var(--fg-dim)" }}>
                    seq {rollbackTarget.revision.revisionSeq} · published{" "}
                    {rollbackTarget.revision.publishedAt} by{" "}
                    {rollbackTarget.revision.publishedBy}
                  </span>
                </>
              ),
            },
            {
              key: "rollback distance",
              value:
                rollbackDistance === null ? (
                  <span style={{ color: "var(--fg-dim)" }}>
                    UNKNOWN — the revision list does not name a current revision
                  </span>
                ) : (
                  <>
                    {rollbackDistance} publish{rollbackDistance === 1 ? "" : "es"}{" "}
                    back
                    {rollbackDistance > 1 && (
                      <span style={{ color: "var(--warn)" }}>
                        {" "}
                        — this walks past {rollbackDistance - 1} revision
                        {rollbackDistance === 2 ? "" : "s"} someone published on
                        purpose
                      </span>
                    )}
                  </>
                ),
            },
            {
              key: "target digest",
              value: <Hash value={rollbackTarget.revision.documentHash} />,
            },
          ]}
          requireTypedConfirmation={
            lane === "production"
              ? {
                  expected: productId,
                  label: `Type "${productId}" to confirm a PRODUCT rollback`,
                }
              : undefined
          }
          onRequestApproval={() =>
            createProposal(productId, {
              operation: "rollback",
              revisionId: rollbackTarget.revision.revisionId,
            })
          }
          onExecute={async (proposalId) => {
            const r = await rollback(
              productId,
              rollbackTarget.revision.revisionId,
              proposalId,
            );
            setRollbackTarget(null);
            setNotice(
              `Live pointer moved ${r.fromRevisionId} → ${r.toRevisionId}. Neither revision was rewritten.`,
            );
            await load();
            // Re-read the tier rather than assume the move took effect.
            // Immediately after a rollback this will often read OUT OF SYNC
            // for the length of the freshness window, which is the true state
            // and the one an operator watching a rollback land actually needs.
            void observe();
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Btn onClick={() => setRollbackTarget(null)}>
                Choose a different revision
              </Btn>
            </div>
            {published === null ? (
              <div style={{ color: "var(--warn)" }}>
                The live revision could not be read, so this screen cannot show
                what would change. The target revision above is still exact —
                its id, sequence and digest are what the approval will commit
                to — but the difference between it and what is live is unknown,
                and is not shown as &ldquo;no changes&rdquo;.
              </div>
            ) : rollbackDiff === null ? (
              <div style={{ color: "var(--fg-dim)" }}>Loading the target document…</div>
            ) : (
              <div
                style={{
                  border: "1px solid var(--border-faint, #20262e)",
                  borderRadius: 4,
                  padding: 10,
                  background: "var(--bg-faint, #11151a)",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  What devices on <b>{laneLabel(lane)}</b> will see:{" "}
                  {summarize(rollbackDiff)}
                </div>
                {/* The field-level list, not only the count. A rollback that
                    reads "3 modified" tells an operator how MUCH changes and
                    nothing about WHAT — and the field that matters is usually
                    one word inside one row. Publish has shown this since it
                    existed; rollback moves the same pointer over the same
                    documents and was reviewed with less. */}
                {rollbackChanges.length > 0 && (
                  <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                    {rollbackChanges.map((c) => (
                      <li key={c.key}>
                        <b>{c.itemId}</b>{" "}
                        <span style={{ color: "var(--fg-dim)" }}>{c.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {rollbackDiff.visibility.length === 0 ? (
                  <div style={{ color: "var(--fg-dim)" }}>
                    No row changes whether it renders on this lane.
                  </div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {rollbackDiff.visibility.map((v) => (
                      <li key={`${v.itemId}-${v.lane}`}>
                        <b>{v.itemId}</b>{" "}
                        {v.isVisible ? (
                          <span style={{ color: "var(--ok)" }}>returns</span>
                        ) : (
                          <span style={{ color: "var(--danger)" }}>
                            disappears
                            {v.reason ? ` — ${v.reason}` : ""}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </GovernedAction>
      )}

      {/* ── §17 BOOTSTRAP ───────────────────────────────────────────────
          Genesis. Reviewable here in full; executed only once the write gate
          is open AND a proposal for this exact act has been approved. */}
      {bootstrapOpen && detail && (
        <GovernedAction
          act={{
            operation: "bootstrap",
            productId,
            lane,
            // THE DIGEST IS THE SERVER'S, AND IT IS THE THING BEING APPROVED.
            //
            // This used to be `null`, on the reasoning that the genesis
            // document embeds a revision id and a publish timestamp the server
            // assigns, so no client can know the stored hash in advance. Half
            // of that is true and the conclusion did not follow: the CONTENT —
            // the entries that reach every device — is derivable by reading
            // the Product Registry, and that is what a signer is approving. The
            // backend derives it read-only, hashes it with the same function
            // it uses to decide whether a re-publish is a no-op, and refuses
            // at execution if the source has moved since.
            //
            // Read from the preflight. Never computed here: this console
            // displays the digest, it is not the authority for it.
            target: genesis?.target ?? null,
          }}
          title="Bootstrap the first revision"
          tone="warning"
          executeLabel="Bootstrap rev_000001"
          blockedReason={
            detail.current !== null
              ? `This product already has a published revision (${detail.current.revisionId}). Bootstrap is refused once any revision exists.`
              : genesis === null
                ? "The genesis content has not been read yet, so there is no digest to approve. Read the migration source above first."
                : genesis.alreadyPublished !== null
                  ? `The server reports ${genesis.alreadyPublished} already published. Bootstrap is refused once any revision exists.`
                  : null
          }
          consequence={
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div>
                Reads the Product Registry as it stands right now and publishes
                its current standalone products as <code>rev_000001</code>. A
                capability is represented through its parent product; a future,
                retired, infrastructure or disabled entry is reported with that
                reason, never dropped silently. The legacy{" "}
                <code>explorer_sites</code> table is not consulted.
              </div>
              <div>
                Until this runs, <code>/v2/explorer/sites</code> is served from
                that legacy table, so no installed app is affected by its
                absence.
              </div>
            </div>
          }
          rows={[
            { key: "action", value: "bootstrap" },
            { key: "product", value: productId },
            { key: "environment", value: laneLabel(lane) },
            { key: "current revision", value: detail.current?.revisionId ?? "none" },
            {
              key: "target",
              value: (
                <>
                  <code>rev_000001</code>, minted from the Product Registry
                  ({genesis?.migrated.source ?? "control_plane_products"}) on this lane
                </>
              ),
            },
            {
              key: "registry entries",
              value:
                genesis === null ? (
                  <span style={{ color: "var(--fg-dim)" }}>not read yet</span>
                ) : (
                  <>
                    {genesis.migrated.carriedCount} carried of{" "}
                    {genesis.migrated.sourceRowCount}
                    {genesis.migrated.dropped.length > 0 && (
                      <span style={{ color: "var(--warn)" }}>
                        {" "}
                        · {genesis.migrated.dropped.length} not carried
                      </span>
                    )}
                  </>
                ),
            },
            {
              key: "content digest",
              value:
                genesis === null ? (
                  <span style={{ color: "var(--fg-dim)" }}>
                    not read yet — the approval cannot be prepared without it
                  </span>
                ) : (
                  <>
                    <Hash value={genesis.contentDigest} />{" "}
                    <span style={{ color: "var(--fg-dim)" }}>
                      — server-derived. The stored revision&rsquo;s own hash also
                      covers the revision id and publish timestamp, which are
                      assigned at write time; this digest is over the content
                      those two exclude, and it is what the approval commits to.
                    </span>
                  </>
                ),
            },
          ]}
          requireTypedConfirmation={
            lane === "production"
              ? {
                  expected: productId,
                  label: `Type "${productId}" to confirm a PRODUCT bootstrap`,
                }
              : undefined
          }
          onRequestApproval={() =>
            createProposal(productId, { operation: "bootstrap" })
          }
          onExecute={async (proposalId) => {
            const r = await bootstrap(productId, proposalId);
            const dropped = r.migrated.dropped;
            setBootstrapOpen(false);
            setNotice(
              `Published ${r.revisionId} from ${r.migrated.carriedCount} of ${r.migrated.sourceRowCount} registry entries.` +
                (dropped.length > 0
                  ? ` ${dropped.length} entr${dropped.length === 1 ? "y was" : "ies were"} not carried: ${dropped
                      .map((d) => `${d.id} (${d.reason})`)
                      .join("; ")}`
                  : " Every entry was carried."),
            );
            await load();
            void observe();
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Btn onClick={() => void loadGenesis()}>
                {genesis === null ? "Read the registry" : "Re-read"}
              </Btn>
              <Btn onClick={() => setBootstrapOpen(false)}>Close</Btn>
              <span style={{ color: "var(--fg-dim)" }}>
                <code>GET .../bootstrap/preview</code> — the server derives the
                genesis document from the Product Registry and writes nothing
              </span>
            </div>

            {genesisError !== null && (
              <div style={{ color: "var(--danger)" }}>
                The genesis content could not be read: {genesisError}. It is
                therefore unreviewed, and there is no digest to approve — that
                is a reason to stop, not a reason to proceed.
              </div>
            )}

            {genesis !== null && (
              <div
                style={{
                  border: "1px solid var(--border-faint, #20262e)",
                  borderRadius: 4,
                  padding: 10,
                  background: "var(--bg-faint, #11151a)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div>
                  <b>{genesis.migrated.carriedCount}</b> of{" "}
                  <b>{genesis.migrated.sourceRowCount}</b> registry entries would be
                  carried into <code>rev_000001</code> on {laneLabel(genesis.lane)} —
                  the current standalone products, from{" "}
                  <code>{genesis.migrated.source}</code>.
                </div>

                {genesis.migrated.dropped.length > 0 && (
                  <div>
                    {/* Reported, never swallowed. An operator approving genesis
                        is approving an omission as much as an inclusion. */}
                    <div style={{ color: "var(--warn)", marginBottom: 4 }}>
                      These registry entries are not carried and will not exist
                      in the first revision, each for the reason the server
                      states:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {genesis.migrated.dropped.map((d) => (
                        <li key={d.id}>
                          <b>{d.id}</b>{" "}
                          <span style={{ color: "var(--fg-dim)" }}>— {d.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <div style={{ color: "var(--fg-dim)", marginBottom: 4 }}>
                    The entries themselves — a count says how many reach a
                    device, not which:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {rowsOf(genesis.document).map((item) => (
                      <li key={item.id}>
                        <b>{item.id}</b>{" "}
                        <span style={{ color: "var(--fg-dim)" }}>
                          {item.title} · {item.environment} · {item.model ? "order" : "sort"} {item.sortOrder}
                          {item.catalog && !item.catalog.enabled ? " · disabled" : ""}
                          {item.minAppVersion ? ` · min ${item.minAppVersion}` : ""}
                        </span>
                        <div style={{ color: "var(--fg-dim)" }}>{item.url}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </GovernedAction>
      )}
    </div>
  );
}
